import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowRight, Search } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue'
import { ConceptChip } from '../components/ConceptBadges'
import { ConceptPicker } from '../components/ConceptPicker'
import { KnowledgeFrame } from '../components/KnowledgeNav'
import { ArtworkTile } from '../components/ArtworkTile'
import { categoryColor } from '../components/categoryStyle'
import { useArtistFingerprint, useArtistSearch, useArtworkComparison, useConceptPath } from '../hooks/useKnowledge'
import type { Concept, WeightedConcept } from '../api/knowledgeApi'

type Picked = Pick<Concept, 'ucid' | 'name' | 'category' | 'artworkCount'>

function ArtistSearch({ onPick }: { onPick: (id: number) => void }) {
  const [q, setQ] = useState('')
  const query = useDebouncedValue(q, 250)
  const { data } = useArtistSearch(query)
  return (
    <div className="relative max-w-md">
      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
      <Input className="pl-8" placeholder="Find an artist…" value={q} onChange={(e) => setQ(e.target.value)} />
      {query.length > 1 && data && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border bg-white shadow-lg">
          {data.data.length === 0 && <div className="px-3 py-2 text-sm text-slate-500">No artist in the graph matches.</div>}
          {data.data.map((a) => (
            <button
              key={a.id}
              onClick={() => { onPick(Number(a.id)); setQ('') }}
              className="flex w-full justify-between px-3 py-1.5 text-left text-sm hover:bg-slate-50"
            >
              {a.name}
              <span className="text-xs tabular-nums text-slate-400">{a.artworksInGraph} artworks</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function ArtistTab() {
  const [params, setParams] = useSearchParams()
  const artistId = Number(params.get('artist')) || undefined
  const pick = (id: number) => setParams({ tab: 'artist', artist: String(id) })
  const { data, isLoading } = useArtistFingerprint(artistId)
  const totalScore = (data?.categories ?? []).reduce((t, c) => t + c.score, 0) || 1

  return (
    <div className="space-y-4">
      <ArtistSearch onPick={pick} />
      {artistId && isLoading && <Skeleton className="h-64 w-full" />}
      {data && (
        <div className="grid gap-6 lg:grid-cols-3">
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                <Link to={`/app/artists/${data.artist.id}`} className="hover:underline">{data.artist.name}</Link>
              </CardTitle>
              <CardDescription>
                {data.artist.artworksInGraph} of {data.artist.artworks} artworks are in the graph. The concepts below
                characterise the work: how often they appear, weighted by how specific they are.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="mb-1 text-xs text-slate-500">Category mix</div>
                <div className="flex h-3 overflow-hidden rounded-full bg-slate-100">
                  {data.categories.map((c) => (
                    <div
                      key={c.category}
                      title={`${c.category} ${Math.round((c.score / totalScore) * 100)}%`}
                      style={{ width: `${(c.score / totalScore) * 100}%`, background: categoryColor(c.category === 'uncategorised' ? null : c.category) }}
                    />
                  ))}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                  {data.categories.slice(0, 8).map((c) => (
                    <span key={c.category} className="inline-flex items-center gap-1">
                      <span className="h-2 w-2 rounded-full" style={{ background: categoryColor(c.category === 'uncategorised' ? null : c.category) }} />
                      {c.category} {Math.round((c.score / totalScore) * 100)}%
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {data.concepts.map((c) => (
                  <ConceptChip key={c.ucid} concept={c} extra={`${c.artworks}`} />
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Artists with similar work</CardTitle>
              <CardDescription>By the same characteristic concepts (weighted cosine similarity).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-1">
              {data.similar.length === 0 && <p className="text-sm text-slate-500">None found.</p>}
              {data.similar.map((a) => (
                <button key={a.id} onClick={() => pick(Number(a.id))} className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm hover:bg-slate-50">
                  <span className="truncate">{a.name}</span>
                  <span className="shrink-0 text-xs tabular-nums text-slate-500">
                    {a.similarity != null ? `${Math.round(a.similarity * 100)}%` : '—'} · {a.shared} shared
                  </span>
                </button>
              ))}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}

function PathTab() {
  const [from, setFrom] = useState<Picked | null>(null)
  const [to, setTo] = useState<Picked | null>(null)
  const { data, isFetching } = useConceptPath(from?.ucid, to?.ucid)
  return (
    <div className="space-y-4">
      <div className="grid max-w-3xl gap-3 sm:grid-cols-2">
        <ConceptPicker value={from} onChange={setFrom} placeholder="From concept…" />
        <ConceptPicker value={to} onChange={setTo} placeholder="To concept…" exclude={from ? [from.ucid] : []} />
      </div>
      {isFetching && <Skeleton className="h-24 w-full" />}
      {data && !isFetching && (
        <Card>
          <CardContent className="space-y-3 p-4">
            {data.steps ? (
              <>
                <div className="text-sm text-slate-600">
                  {data.steps.length - 1} step{data.steps.length === 2 ? '' : 's'} through concept-to-concept links (similarity on each link).
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {data.steps.map((s, i) => (
                    <span key={s.ucid} className="inline-flex items-center gap-2">
                      {i > 0 && (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-400">
                          <ArrowRight className="h-3.5 w-3.5" />
                          {s.similarity != null && s.similarity.toFixed(2)}
                        </span>
                      )}
                      <ConceptChip concept={s as WeightedConcept} />
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-600">No chain of links within six steps.</p>
            )}
            <p className="text-xs text-slate-500">
              {data.sharedArtworks.toLocaleString()} artworks carry both concepts.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function ConceptList({ title, items }: { title: string; items: WeightedConcept[] }) {
  return (
    <div>
      <div className="mb-2 text-xs font-medium text-slate-600">{title} ({items.length})</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((c) => <ConceptChip key={c.ucid} concept={c} extra={c.weight != null ? c.weight.toFixed(2) : undefined} />)}
        {items.length === 0 && <span className="text-xs text-slate-400">none</span>}
      </div>
    </div>
  )
}

function CompareTab() {
  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const ida = Number(useDebouncedValue(a, 400)) || undefined
  const idb = Number(useDebouncedValue(b, 400)) || undefined
  const { data, isFetching, error } = useArtworkComparison(ida, idb)
  return (
    <div className="space-y-4">
      <div className="flex max-w-md gap-3">
        <Input inputMode="numeric" placeholder="Artwork id" value={a} onChange={(e) => setA(e.target.value.replace(/\D/g, ''))} />
        <Input inputMode="numeric" placeholder="Artwork id" value={b} onChange={(e) => setB(e.target.value.replace(/\D/g, ''))} />
      </div>
      {error && <p className="text-sm text-rose-600">One of those artworks does not exist.</p>}
      {isFetching && <Skeleton className="h-48 w-full" />}
      {data && !isFetching && (
        <div className="grid gap-6 lg:grid-cols-[12rem_1fr_12rem]">
          <ArtworkTile artwork={data.a} />
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">{Math.round(data.overlap * 100)}% overlap</CardTitle>
              <CardDescription>Shared concepts as a share of all their concepts, each weighted by how specific it is (the number on each chip).</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <ConceptList title="Shared" items={data.shared} />
              <ConceptList title={`Only “${data.a.name}”`} items={data.onlyA} />
              <ConceptList title={`Only “${data.b.name}”`} items={data.onlyB} />
            </CardContent>
          </Card>
          <ArtworkTile artwork={data.b} />
        </div>
      )}
    </div>
  )
}

export function ExplorePage() {
  const [params, setParams] = useSearchParams()
  const tab = params.get('tab') || 'artist'
  return (
    <KnowledgeFrame
      title="Explore"
      description="Look at the graph from other angles: what characterises an artist and who is similar, how two concepts connect, and what two artworks have in common. Concepts count by their search weight, so generic ones barely register."
    >
      <Tabs value={tab} onValueChange={(t) => setParams({ tab: t })}>
        <TabsList>
          <TabsTrigger value="artist">Artist fingerprint</TabsTrigger>
          <TabsTrigger value="path">Concept path</TabsTrigger>
          <TabsTrigger value="compare">Compare artworks</TabsTrigger>
        </TabsList>
        <TabsContent value="artist" className="pt-4"><ArtistTab /></TabsContent>
        <TabsContent value="path" className="pt-4"><PathTab /></TabsContent>
        <TabsContent value="compare" className="pt-4"><CompareTab /></TabsContent>
      </Tabs>
    </KnowledgeFrame>
  )
}
